import React, { useMemo } from 'react';
import { Select } from 'antd';
import MeidouIcon from '@/assets/image/meidou/meidou-price-bg.png';
import classnames from 'classnames';

import styles from './index.module.css';
import { useAtom } from 'jotai';
import { giftLevelAtom } from '@/store';
import { track } from '@/utils/common';
import { useLocaleStore } from '@/i18n/localeStore';

type LevelOption = {
  value: number;
  label: string;
};

interface MeidouPanelProps {
  /** 每月美豆数量，如 "330美豆/月" */
  meidouText: string;
  /** 资源量描述，如 "约可生成500张图或120个视频" */
  resourceDesc?: string;
  /** 单价说明，如 "仅需¥0.041/豆" */
  unitPrice?: string;
  /** 标签列表 */
  tags?: string[];
  /** 是否可展开（高级版带下拉箭头） */
  expandable?: boolean;
  /** 美豆档位下拉选项（高级版） */
  levelOptions?: LevelOption[];
}

const MeidouPanel: React.FC<MeidouPanelProps> = ({
  meidouText,
  resourceDesc,
  unitPrice,
  tags = [],
  expandable = false,
  levelOptions = [],
}) => {
  const { $t } = useLocaleStore();
  const [giftLevel, setGiftLevel] = useAtom(giftLevelAtom);

  const selectedMeidouText = useMemo(() => {
    if (!expandable || levelOptions.length === 0) {
      return meidouText;
    }
    return levelOptions.find((item) => item.value === giftLevel)?.label || meidouText;
  }, [expandable, levelOptions, giftLevel, meidouText]);

  return (
    <div className={styles.panel}>
      <div className={classnames(styles.header, { [styles.headerExpandable]: expandable })}>
        <div className={styles.meidouRow}>
          <img src={MeidouIcon} alt="Meidou Icon" className={styles.meidouIcon} />
          {expandable && levelOptions.length > 0 ? (
            <Select
              value={giftLevel}
              size="small"
              options={levelOptions}
              className={styles.meidouLevelSelect}
              onChange={(value) => {
                setGiftLevel(value);
                track({
                  event_id: 'vip_page_click',
                  event_name: '会员落地页点击',
                  params: { class: `high_level${value}` },
                });
              }}
              dropdownClassName="meidouLevelDropdown"
              suffixIcon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M1.46967 3.84467C1.76256 3.55178 2.23744 3.55178 2.53033 3.84467L6 7.31434L9.46967 3.84467C9.76256 3.55178 10.2374 3.55178 10.5303 3.84467C10.8232 4.13756 10.8232 4.61244 10.5303 4.90533L6.88388 8.55178C6.39573 9.03993 5.60427 9.03993 5.11612 8.55178L1.46967 4.90533C1.17678 4.61244 1.17678 4.13756 1.46967 3.84467Z"
                    fill="white"
                    fillOpacity="0.5"
                  />
                </svg>
              }
            />
          ) : (
            <span className={styles.meidouText}>{selectedMeidouText}</span>
          )}
        </div>
      </div>

      {(resourceDesc || unitPrice || tags.length > 0) && (
        <div className={styles.body}>
          {unitPrice && (
            <div className={styles.priceRow}>
              <span className={styles.resourceLabel}>{$t((v) => v.Label_EstimatedConsumption)}</span>
              <span className={styles.unitPrice}>{unitPrice}</span>
            </div>
          )}

          {resourceDesc && <p className={styles.resourceDesc}>{resourceDesc}</p>}

          {tags.length > 0 && (
            <div className={styles.tags}>
              {tags.map((tag, idx) => (
                <span key={idx} className={styles.tag}>
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(MeidouPanel);
